import { useEffect, useRef, useState } from 'react';

import { AllProps, DeferWrapperProps, ExpectedComponent } from './index.types';

export function useDeferWrapperController<
  T,
  Props extends AllProps<T>,
  K extends ExpectedComponent<Props>,
>(props: DeferWrapperProps<T, Props, K>) {
  // Delegate to smaller hooks
  const valueState = useValueState(props);
  const popperState = usePopper(valueState.hasPendingChanges);
  const actions = useActions(props, valueState, popperState);

  return {
    valueState,
    popperState,
    actions,
  };
}

function useValueState<T>(props: DeferWrapperProps<T, any, any>) {
  const { latestValue } = props;
  const [internalValue, setInternalValue] = useState<T>(latestValue);

  // Update internal value when latest value changes
  useEffect(() => {
    setInternalValue(latestValue);
  }, [latestValue]);

  // Check if there's a difference between internal and latest values
  const hasPendingChanges =
    JSON.stringify(internalValue) !== JSON.stringify(latestValue);

  const handleInternalChange = (value: T) => {
    setInternalValue(value);
  };

  return {
    internalValue,
    setInternalValue,
    hasPendingChanges,
    handleInternalChange,
  };
}

function usePopper(shouldShow: boolean) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (shouldShow && wrapperRef.current) {
      setAnchorEl(wrapperRef.current);
    } else {
      setAnchorEl(null);
    }
  }, [shouldShow, wrapperRef]);

  const hidePopper = () => {
    setAnchorEl(null);
  };

  return {
    anchorEl,
    hidePopper,
    wrapperRef,
  };
}

function useActions<T>(
  props: DeferWrapperProps<T, any, any>,
  valueState: ReturnType<typeof useValueState<T>>,
  popperState: ReturnType<typeof usePopper>,
) {
  const { latestValue, onChange } = props;

  const handleConfirm = () => {
    onChange(valueState.internalValue);
    popperState.hidePopper();
  };

  const handleRevert = () => {
    valueState.setInternalValue(latestValue);
    popperState.hidePopper();
  };

  return {
    handleConfirm,
    handleRevert,
  };
}
